import { X } from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

/**
 * Usa `<dialog>` nativo: o navegador entrega prisão de foco, `Esc` para fechar
 * e retorno do foco ao gatilho — comportamentos exigidos por design.md §12
 * que reimplementados em JavaScript sempre saem incompletos.
 *
 * Sem borda e sem divisórias: cabeçalho, corpo e rodapé se separam por espaço.
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  flush = false,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  size?: 'md' | 'lg' | 'xl' | undefined;
  /**
   * Tira o respiro lateral do corpo.
   *
   * Serve ao formulário em etapas, cuja trilha de abas ocupa a lateral inteira
   * do modal: com o padding do corpo, a trilha ficaria flutuando a 24px da
   * borda, como um painel solto em vez da margem da caixa.
   */
  flush?: boolean | undefined;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();

      /*
       * `showModal` põe o foco no primeiro elemento focável, que aqui é o "X"
       * de fechar — e o anel laranja aparecia em cima dele toda vez que um
       * modal abria, como se sair fosse a ação sugerida.
       *
       * A busca é dentro do corpo, e não do diálogo inteiro, justamente para
       * pular o "X" do cabeçalho. E alcança `button` porque nem todo primeiro
       * controle é campo de texto: no "Incluir na equipe" é um menu suspenso, e
       * com a busca restrita a `input` o foco voltava a cair no fechar.
       *
       * Aba fica de fora. No cadastro de obra o primeiro botão do corpo passou
       * a ser uma aba da trilha lateral, e o foco caía nela — o anel laranja
       * aparecia sobre o sumário, em vez de sobre o nome da obra, que é onde se
       * começa a escrever. Aba é navegação, não campo.
       *
       * Marcar o campo com `autoFocus` não resolveria: o React não escreve o
       * atributo no DOM, ele chama `.focus()` na montagem — e este efeito, que
       * roda depois, tomaria o foco de volta.
       */
      dialog
        .querySelector<HTMLElement>(
          '[data-modal-body] :is(input, textarea, select, button):not([role="tab"])',
        )
        ?.focus();
    }

    if (!open && dialog.open) dialog.close();
  }, [open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    // `Esc` dispara `cancel`; sem isto o React ficaria com `open` dessincronizado.
    const handleCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };

    dialog.addEventListener('cancel', handleCancel);
    return () => dialog.removeEventListener('cancel', handleCancel);
  }, [onClose]);

  return (
    <dialog
      ref={dialogRef}
      aria-labelledby="modal-title"
      className={cn(
        /*
         * `m-auto` é o que centraliza. O navegador centraliza um <dialog> modal
         * com `inset: 0; margin: auto`, mas o preflight do Tailwind zera a
         * margem de todo elemento — sem isto o modal cola no canto superior
         * esquerdo.
         */
        'm-auto w-[calc(100vw-2rem)] rounded-xl bg-card p-0 text-foreground',
        'backdrop:bg-[rgba(24,24,27,0.45)]',
        // Em tela pequena vira bottom sheet: margem embaixo zerada puxa o modal para baixo.
        'max-sm:mx-0 max-sm:mb-0 max-sm:w-full max-sm:max-w-full max-sm:rounded-b-none',
        size === 'xl' ? 'max-w-[1040px]' : size === 'lg' ? 'max-w-[880px]' : 'max-w-[560px]',
      )}
      onClick={(event) => {
        // Clique no backdrop atinge o próprio <dialog>, não seu conteúdo.
        if (event.target === dialogRef.current) onClose();
      }}
    >
      <div className="flex max-h-[85dvh] flex-col">
        <header className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
          <div>
            <h2 id="modal-title" className="text-[17px] font-semibold tracking-tight">
              {title}
            </h2>
            {description ? (
              <p className="mt-1 text-[13px] text-muted-foreground">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="-mt-1 -mr-2 grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <X size={18} strokeWidth={1.75} aria-hidden />
          </button>
        </header>

        <div
          data-modal-body
          className={cn('flex-1 overflow-y-auto', flush ? 'px-0 pb-0' : 'px-6 pb-2')}
        >
          {children}
        </div>

        {footer ? (
          <footer className="flex items-center justify-end gap-2 px-6 pt-4 pb-6">{footer}</footer>
        ) : null}
      </div>
    </dialog>
  );
}
